import { useNavigate } from "react-router-dom";
import { shortDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/common/SearchBar";
import { ClientFilesPanel } from "@/pages/clients/sections/ClientFilesPanel";
import { useAuth } from "@/auth/AuthContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Eye, FileDown, Mail, FileText, ArrowUp, ArrowDown, ArrowUpDown,
} from "lucide-react";
import { downloadPdfUrl } from "@/lib/downloadBlob";
import { getExecutedProposalDocument } from "@/lib/api";

// Per-doc type → pill color. Identical to the prior inline map so the
// visual UX is byte-for-byte preserved.
const TYPE_COLORS = {
  "Executed Work Order": "bg-green-50 text-green-700 border-green-200",
  "Work Order":          "bg-blue-50 text-blue-700 border-blue-200",
  "Proposal":            "bg-purple-50 text-purple-700 border-purple-200",
  "Purchase Order":      "bg-amber-50 text-amber-700 border-amber-200",
  "Inspection Report":   "bg-teal-50 text-teal-700 border-teal-200",
  "Change Order":        "bg-orange-50 text-orange-700 border-orange-200",
};
// Display only — the stored type string is still used for filters, mail subjects and titles.
const TYPE_LABEL = {
  "Executed Work Order": "Executed work order", "Work Order": "Work order", "Purchase Order": "Purchase order",
  "Inspection Report": "Inspection report", "Change Order": "Change order",
};

// Documents tab on the customer side-sheet. Lists every PDF-backed
// record (Proposals, WOs, Change Orders, POs, Inspections) with quick
// preview / download / email actions. Extracted from Clients.jsx
// (Feb 2026, iter175) to whittle the 3,683-LOC monster down.
const HISTORY_DUPLICATE_TYPES = new Set(["Work Order", "Proposal"]);

export function ClientDocumentsTab({
  selectedClient,
  clientDocuments = [],
  clientWorkOrders = [],
  docSearch, setDocSearch,
  docSort, setDocSort,
  setPendingViewWO,
  setActiveDetailTab,
  setDocPreview,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Toggle a sort column through: asc → desc → off, like a 3-state
  // checkbox.  When we hit the "off" state we clear `field` to null
  // too — otherwise the icon condition `docSort.field === <col>` still
  // matches and renders ArrowDown instead of the neutral ArrowUpDown.
  const toggleSort = (field) => {
    setDocSort((prev) => {
      if (prev.field !== field) return { field, dir: "asc" };
      if (prev.dir === "asc") return { field, dir: "desc" };
      if (prev.dir === "desc") return { field: null, dir: null };
      return { field, dir: "asc" };
    });
  };

  // Filter + sort the doc list.
  // Work orders and proposals already have their own History lists — only the PDFs that have no other home show here.
  let filtered = clientDocuments.filter((d) => !HISTORY_DUPLICATE_TYPES.has(d.type)).filter((d) => {
    if (!docSearch) return true;

    const q = docSearch.toLowerCase();
    return (
      (d.name || "").toLowerCase().includes(q)
      || (d.type || "").toLowerCase().includes(q)
      || (d.status || "").toLowerCase().includes(q)
    );
  });
  if (docSort.field) {
    filtered = [...filtered].sort((a, b) => {
      let cmp;
      if (docSort.field === "type") cmp = (a.type || "").localeCompare(b.type || "");
      else if (docSort.field === "date") cmp = (a.date || "").localeCompare(b.date || "");
      else cmp = (a.status || "").localeCompare(b.status || "");
      return docSort.dir === "asc" ? cmp : -cmp;
    });
  }

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <SearchBar value={docSearch} onChange={setDocSearch} placeholder="Search documents..." testId="doc-search" />
        <span className="ml-auto text-xs text-slate-500 num">{filtered.length} document{filtered.length === 1 ? "" : "s"}</span>
      </div>
      <ClientFilesPanel clientId={selectedClient?.id} canWrite={user?.role !== "technician"} search={docSearch} />
      <h5 className="text-xs font-semibold text-slate-500">Generated documents</h5>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm border rounded-lg">
          <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          {docSearch ? "No documents match your search" : "No documents yet"}
        </div>
      ) : (
        <div className="data-table-container"><Table>
          <TableHeader>
            <TableRow>
              <TableHead className="th-std w-40">
                <button
                  className="flex items-center gap-1 hover:text-slate-900 transition-colors"
                  onClick={() => toggleSort("type")}
                  data-testid="doc-sort-type"
                >
                  Type
                  {docSort.field === "type"
                    ? (docSort.dir === "asc"
                        ? <ArrowUp className="w-3 h-3" />
                        : <ArrowDown className="w-3 h-3" />)
                    : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                </button>
              </TableHead>
              <TableHead className="th-std">Name</TableHead>
              <TableHead className="th-std w-28">
                <button
                  className="flex items-center gap-1 hover:text-slate-900 transition-colors"
                  onClick={() => toggleSort("date")}
                  data-testid="doc-sort-date"
                >
                  Date
                  {docSort.field === "date"
                    ? (docSort.dir === "asc"
                        ? <ArrowUp className="w-3 h-3" />
                        : <ArrowDown className="w-3 h-3" />)
                    : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                </button>
              </TableHead>
              <TableHead className="th-std w-24">
                <button
                  className="flex items-center gap-1 hover:text-slate-900 transition-colors"
                  onClick={() => toggleSort("status")}
                  data-testid="doc-sort-status"
                >
                  Status
                  {docSort.field === "status"
                    ? (docSort.dir === "asc"
                        ? <ArrowUp className="w-3 h-3" />
                        : <ArrowDown className="w-3 h-3" />)
                    : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                </button>
              </TableHead>
              <TableHead className="th-std w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((doc) => {
              // Navigate handler — opens the source record in its tab.
              const openDoc = () => {
                if (doc.ref_type === "work_order"
                    || doc.ref_type === "change_order"
                    || doc.ref_type === "purchase_order") {
                  const wo = clientWorkOrders.find((w) => w.id === doc.ref_id);
                  if (wo) { setPendingViewWO(wo); setActiveDetailTab("workorders"); }
                } else if (doc.ref_type === "proposal") {
                  navigate("/proposals", {
                    state: {
                      viewProposalId: doc.ref_id,
                      returnToClient: { clientId: selectedClient?.id, tab: "documents" },
                    },
                  });
                } else if (doc.ref_type === "inspection") {
                  setActiveDetailTab("inspections");
                }
              };

              // Resolve a PDF src (URL or data URI) for in-app modal preview.
              const resolveDocPdfSrc = () => {
                const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
                if (doc.ref_type === "proposal" && doc.has_pdf) {
                  return getExecutedProposalDocument(doc.ref_id);
                }
                if (doc.ref_type === "purchase_order" && doc.po_id) {
                  return `${baseUrl}/api/work-orders/${doc.ref_id}/purchase-orders/${doc.po_id}/pdf`;
                }
                const wo = clientWorkOrders.find((w) => w.id === doc.ref_id);
                if (doc.ref_type === "work_order" && wo?.executed_wo_pdf) return wo.executed_wo_pdf;
                if (doc.ref_type === "change_order" && wo) {
                  const co = (wo.change_orders || []).find((x) => x.id === doc.co_id);
                  if (co?.executed_co_pdf) return co.executed_co_pdf;
                }
                return null;
              };

              // Download handler — triggers a real file download per doc type.
              const downloadDocPdf = async () => {
                if (doc.ref_type === "proposal") {
                  await downloadPdfUrl(`/proposals/${doc.ref_id}/executed-document?download=1`, {
                    suggestedName: `${doc.name || "Proposal"}.pdf`,
                    label: "proposal",
                  });
                  return;
                }
                if (doc.ref_type === "purchase_order" && doc.po_id) {
                  await downloadPdfUrl(`/work-orders/${doc.ref_id}/purchase-orders/${doc.po_id}/pdf?download=1`, {
                    suggestedName: `${doc.name || "PO"}.pdf`,
                    label: "purchase order",
                  });
                  return;
                }
                const wo = clientWorkOrders.find((w) => w.id === doc.ref_id);
                // WO/CO executed PDFs are stored as base64 data URIs on
                // the record itself — data URIs are already "the file",
                // so the <a download> anchor pattern works natively and
                // doesn't need the async helper.
                if (doc.ref_type === "work_order" && wo?.executed_wo_pdf) {
                  const a = document.createElement("a");
                  a.href = wo.executed_wo_pdf;
                  a.download = `${wo.wo_number}_Executed.pdf`;
                  a.rel = "noopener";
                  document.body.appendChild(a);
                  a.click();
                  setTimeout(() => document.body.removeChild(a), 1000);
                  return;
                }
                if (doc.ref_type === "change_order" && wo) {
                  const co = (wo.change_orders || []).find((x) => x.id === doc.co_id);
                  if (co?.executed_co_pdf) {
                    const a = document.createElement("a");
                    a.href = co.executed_co_pdf;
                    a.download = `${wo.wo_number}_CO_Executed.pdf`;
                    a.rel = "noopener";
                    document.body.appendChild(a);
                    a.click();
                    setTimeout(() => document.body.removeChild(a), 1000);
                  }
                }
              };

              // Email handler — mailto with a link (when a public URL
              // exists) or a "see attached" prompt. Per product owner:
              // mailto with "see attached" is acceptable for CO/WO/
              // Proposal (data-URI PDFs).
              const emailDocPdf = () => {
                const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
                const subject = encodeURIComponent(`ABS — ${doc.type}: ${doc.name}`);
                let link = "";
                if (doc.ref_type === "purchase_order" && doc.po_id) {
                  link = `${baseUrl}/api/work-orders/${doc.ref_id}/purchase-orders/${doc.po_id}/pdf`;
                } else if (doc.ref_type === "proposal" && doc.has_pdf) {
                  link = getExecutedProposalDocument(doc.ref_id);
                }
                const bodyLines = link
                  ? [`Hi,`, ``, `Please view the ${doc.type.toLowerCase()} here:`, link, ``, `Thank you,`, `Associated Building Specialties, Inc.`]
                  : [`Hi,`, ``, `Please see the attached ${doc.type.toLowerCase()}.`, ``, `Thank you,`, `Associated Building Specialties, Inc.`];
                const body = encodeURIComponent(bodyLines.join("\n"));
                window.location.href = `mailto:?subject=${subject}&body=${body}`;
              };

              // Open in-app preview modal (no new tab, no route change).
              const openPreview = (e) => {
                e.stopPropagation();
                const src = resolveDocPdfSrc();
                if (!src) { openDoc(); return; }
                setDocPreview({
                  title: `${doc.type} — ${doc.name}`,
                  src,
                  download: downloadDocPdf,
                  email: emailDocPdf,
                });
              };

              return (
                <TableRow
                  key={doc.id}
                  onClick={openDoc}
                  className="cursor-pointer table-row-hover"
                  data-testid={`doc-row-${doc.id}`}
                >
                  <TableCell className="text-xs">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${TYPE_COLORS[doc.type] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {TYPE_LABEL[doc.type] || doc.type}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs font-medium text-brand hover:underline">
                    {doc.name}
                    {/* Engagement sub-line — only rendered when the
                        customer has actually clicked an emailed link.
                        Tracked in /api/r/p/* (Proposal) and /api/r/w/*
                        (WO) redirects. Hidden entirely when view_count
                        is 0 or undefined so the table stays uncluttered
                        for unsent docs. */}
                    {doc.view_count > 0 && (doc.ref_type === "proposal" || doc.ref_type === "work_order") && (
                      <div
                        className="text-[10px] text-slate-400 font-normal mt-0.5"
                        data-testid={`doc-views-${doc.id}`}
                      >
                        Viewed {doc.view_count}×
                        {doc.last_viewed_at
                          ? ` · last ${shortDate(doc.last_viewed_at)}`
                          : ""}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 tabular-nums">
                    {shortDate(doc.date)}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{doc.status}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      {/* Eye button: always opens the in-app preview
                          modal. When the doc has no PDF (e.g. an
                          unsigned WO or an inspection report) we disable
                          the button rather than fall back to navigation
                          — clicking the eye should NEVER take the user
                          away from the Documents tab. The doc name itself
                          remains row-clickable to navigate. */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        title={doc.has_pdf ? "Quick preview" : "No PDF available"}
                        disabled={!doc.has_pdf}
                        onClick={(e) => { e.stopPropagation(); if (doc.has_pdf) openPreview(e); }}
                        data-testid={`doc-view-${doc.id}`}
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                      {doc.has_pdf && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          title="Download PDF"
                          onClick={(e) => { e.stopPropagation(); downloadDocPdf(); }}
                          data-testid={`doc-download-${doc.id}`}
                        >
                          <FileDown className="w-3 h-3" />
                        </Button>
                      )}
                      {doc.has_pdf && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          title="Email PDF"
                          onClick={(e) => { e.stopPropagation(); emailDocPdf(); }}
                          data-testid={`doc-email-${doc.id}`}
                        >
                          <Mail className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table></div>
      )}
    </>
  );
}
